import fetchWithAuth from "@/api/secureFetch";
import type { PersistedNewProductDraftState } from "./state";

const DRAFT_ENDPOINT = "/api/product-creation-draft";
const DRAFTS_ENDPOINT = "/api/product-creation-drafts";

export interface ProductCreationDraftRecord {
    draft_id: number;
    title: string;
    status: "In Progress" | "Submitted";
    version: number;
    project_id: number | null;
    draft_state: PersistedNewProductDraftState;
    created_at: string;
    updated_at: string;
}

export function productCreationDraftProgress(draft: ProductCreationDraftRecord): { completed: number; total: number } {
    const state = draft.draft_state.state;
    const completed = [
        state.fields.name.trim(),
        state.fields.problem.trim(),
        state.fields.solution.trim(),
        state.users.some(value => value.trim()),
        state.team.some(value => value.trim())
    ].filter(Boolean).length;
    return { completed, total: 5 };
}

async function readError(response: Response, fallback: string): Promise<Error> {
    const body = await response.json().catch(() => ({}));
    return new Error(body?.error || fallback);
}

export async function listProductCreationDrafts(token: string): Promise<ProductCreationDraftRecord[]> {
    const response = await fetchWithAuth(DRAFTS_ENDPOINT, token);
    if (!response.ok) throw await readError(response, `Failed to load product drafts (${response.status})`);
    const body = await response.json();
    return body.drafts ?? [];
}

/** Fetch one draft by id; resolves null when it no longer exists. */
export async function getProductCreationDraft(
    token: string,
    draftId: number
): Promise<ProductCreationDraftRecord | null> {
    const response = await fetchWithAuth(`${DRAFT_ENDPOINT}/${draftId}`, token);
    if (response.status === 404) return null;
    if (!response.ok) throw await readError(response, `Failed to load product draft (${response.status})`);
    const body = await response.json();
    return body.draft ?? null;
}

/** Create a new draft (no id) or update an existing one (id provided). */
export async function saveProductCreationDraft(
    token: string,
    draft: PersistedNewProductDraftState,
    draftId?: number | null
): Promise<ProductCreationDraftRecord> {
    const url = draftId == null ? DRAFT_ENDPOINT : `${DRAFT_ENDPOINT}/${draftId}`;
    const response = await fetchWithAuth(url, token, {
        method: draftId == null ? "POST" : "PUT",
        body: JSON.stringify(draft)
    });
    if (!response.ok) throw await readError(response, `Failed to save product draft (${response.status})`);
    const body = await response.json();
    return body.draft;
}

export async function discardProductCreationDraft(token: string, draftId: number): Promise<void> {
    const response = await fetchWithAuth(`${DRAFT_ENDPOINT}/${draftId}`, token, { method: "DELETE" });
    if (!response.ok) throw await readError(response, `Failed to discard product draft (${response.status})`);
}

export async function completeProductCreationDraft(
    token: string,
    draftId: number,
    projectId: number
): Promise<void> {
    const response = await fetchWithAuth(`${DRAFT_ENDPOINT}/${draftId}/complete`, token, {
        method: "POST",
        body: JSON.stringify({ project_id: projectId })
    });
    if (!response.ok) throw await readError(response, `Failed to complete product draft (${response.status})`);
}
